import React, { useState } from 'react';
import {
  View,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
} from 'react-native';
import Text from '../../components/text';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

const SETTINGS_SECTIONS = [
  {
    category: 'ACCOUNT & SECURITY',
    items: [
      {
        id: 'my-account',
        title: 'My Account',
        description: 'Update profile photos, personal details, and verified landlord status credentials.',
        icon: 'user',
        route: 'AccountEdit',
      },
      {
        id: 'change-password',
        title: 'Change Password',
        description: 'Modify your login password and manage two-factor security parameters.',
        icon: 'lock',
        route: 'ChangePassword',
      },
    ],
  },
  {
    category: 'PROPERTY MANAGEMENT',
    items: [
      {
        id: 'hubflow-deposit',
        title: 'HubFlow & Deposit Management',
        description: 'Configure and synchronize landlord custody deposits across government agencies.',
        icon: 'archive',
        route: 'HubFlow',
      },
      {
        id: 'archive-manager',
        title: 'Archive Manager',
        description: 'Access hidden, completed, or deactivated landlord, property, and tenant records.',
        icon: 'inbox',
        route: 'ArchiveManager',
      },
    ],
  },
  {
    category: 'FINANCIAL & BILLING',
    items: [
      {
        id: 'bank-account',
        title: 'Bank Account',
        description: 'Manage default payout channels and coordinate automated rent disbursement structures.',
        icon: 'credit-card',
        route: 'BankAccount',
      },
      {
        id: 'my-invoices',
        title: 'My Invoices',
        description: 'Review system usage receipts, custom property protection subscriptions, and platform ledger.',
        icon: 'file-text',
        route: 'MyInvoices',
      },
    ],
  },
  {
    category: 'SUPPORT & LEGAL',
    items: [
      {
        id: 'contact-support',
        title: 'Contact Us & Support',
        description: 'Direct line to help desk, property lawyers, and specialized technical support.',
        icon: 'headphones',
        route: 'ContactUs',
      },
      {
        id: 'legal-bits',
        title: 'Legal Bits',
        description: 'Review Terms & Conditions, Privacy Policy, and Cookie Policy for the Hubita platform.',
        icon: 'book-open',
        route: 'LegalBits',
      },
    ],
  },
];

const Settings = ({ navigation }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const handleSearch = (text) => {
    setSearchQuery(text);
  };

  const filterItems = (items) => {
    if (!searchQuery || !searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q)
    );
  };

  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]} showsVerticalScrollIndicator={true}>
      {/* Header */}
      <Text style={styles.breadcrumbText}>Settings</Text>

      <View style={styles.headerRow}>
        <Text style={styles.pageTitle}>Settings</Text>
      </View>

      {/* Search Input matching Figma */}
      <View style={styles.searchBox}>
        <Ionicons name="search-outline" size={16} color="#9CA3AF" style={{ marginRight: 10 }} />
        <TextInput
          placeholder="Search for settings, integrations, invoices, documents..."
          placeholderTextColor="#9CA3AF"
          value={searchQuery}
          onChangeText={handleSearch}
          style={styles.searchInput}
        />
      </View>

      {/* Categorized Settings Groups matching Figma */}
      <View style={styles.groupsWrapper}>
        {SETTINGS_SECTIONS.map((section, secIdx) => {
          const visibleItems = filterItems(section.items);
          if (visibleItems.length === 0) return null;

          return (
            <View key={secIdx} style={styles.sectionGroup}>
              <Text style={styles.categoryLabel}>{section.category}</Text>

              {visibleItems.map((item) => (
                <TouchableOpacity
                  key={item.id}
                  style={styles.settingCard}
                  onPress={() => navigation.navigate(item.route, item.params)}
                  activeOpacity={0.85}
                >
                  <View style={styles.cardLeft}>
                    <View style={styles.iconContainer}>
                      <Feather name={item.icon} size={18} color="#6C2BD9" />
                    </View>

                    <View style={styles.textContainer}>
                      <Text style={styles.itemTitle}>{item.title}</Text>
                      <Text style={styles.itemDescription} numberOfLines={2}>
                        {item.description}
                      </Text>
                    </View>
                  </View>

                  <Feather name="chevron-right" size={18} color="#9CA3AF" />
                </TouchableOpacity>
              ))}
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
};

export default drawerHoc({
  Component: Settings,
  showBackButton: false,
  noScroll: true,
});

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  breadcrumbText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
  },

  headerRow: {
    marginBottom: 18,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },

  /* SEARCH BOX */
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 14,
    height: 42,
    marginBottom: 20,
    ...SHADOWS.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
    outlineStyle: 'none',
  },

  groupsWrapper: {
    marginBottom: 40,
  },

  sectionGroup: {
    marginBottom: 20,
  },

  categoryLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#9CA3AF',
    letterSpacing: 0.5,
    marginBottom: 10,
    marginLeft: 2,
  },

  /* SETTING CARD */
  settingCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 14,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...SHADOWS.sm,
  },

  cardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 12,
  },

  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: '#F5F3FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
  },

  textContainer: {
    flex: 1,
  },

  itemTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 2,
  },

  itemDescription: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
  },
});
